import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  ChevronRight, Plus, AlertCircle, CheckCircle2,
  Smartphone, Printer, Share2, FileText, QrCode, Users, UserCog, Maximize2, Circle
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useDemo } from '@/contexts/DemoContext';
import IntroScreen from './components/IntroScreen';
import ProfileSelector from './components/ProfileSelector';
import EmergencySummary from './components/EmergencySummary';
import AddProfileDialog from './components/AddProfileDialog';
import APP_ICONS, { PastelIcon } from '@/utils/appIcons';

/**
 * StatusIcon - Shows completion status for binder sections
 * Moved outside of component to avoid re-render issues
 */
const StatusIcon = ({ status }) => {
  switch (status) {
    case 'completed':
      // Green/brand purple for completed - NOT red
      return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
    case 'incomplete':
      // Blue/purple for in-progress
      return <Circle className="h-5 w-5 text-blue-500 fill-blue-100" />;
    default:
      // Gray for not started
      return <div className="h-5 w-5 rounded-full border-2 border-slate-300" />;
  }
};

/**
 * EmergencyBinderPage - Digital Life Binder
 * Store medical, emergency, document, household, and family information
 */

const EmergencyBinderPage = () => {
  const navigate = useNavigate();
  const demo = useDemo();
  const { isDemoMode, getProfiles: getDemoProfiles } = demo;
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showIntro, setShowIntro] = useState(false);
  const [showAddProfile, setShowAddProfile] = useState(false);
  const [overallCompletion, setOverallCompletion] = useState(0);

  // Fetch profiles on mount or when demo mode changes
  useEffect(() => {
    fetchProfiles();
  }, [isDemoMode, demo.demoData]);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      
      // DEMO MODE: Use local demo data
      if (isDemoMode) {
        const demoProfiles = getDemoProfiles();
        setProfiles(demoProfiles);
        
        if (demoProfiles.length > 0) {
          setSelectedProfile(demoProfiles[0]);
          const completion = demo.getBinderCompletion();
          console.log('Demo completion:', completion); // Debug
          setOverallCompletion(completion?.completion_percentage || 0);
        } else {
          setShowIntro(true);
        }
        setLoading(false);
        return;
      }
      
      // REAL MODE: Fetch from API
      const response = await API.get('/emergency-binder/profiles');
      setProfiles(response.data);
      
      // If no profiles, show intro
      if (response.data.length === 0) {
        setShowIntro(true);
      } else {
        // Select first profile by default
        setSelectedProfile(response.data[0]);
        
        // Calculate overall completion
        const completionResponse = await API.get('/emergency-binder/completion');
        setOverallCompletion(completionResponse.data.completion_percentage);
      }
    } catch (error) {
      console.error('Failed to fetch profiles:', error);
      // Show intro for new users
      setShowIntro(true);
    } finally {
      setLoading(false);
    }
  };

  const handleStartBinder = () => {
    setShowIntro(false);
    setShowAddProfile(true);
  };

  const handleProfileCreated = (newProfile) => {
    setProfiles([...profiles, newProfile]);
    setSelectedProfile(newProfile);
    setShowAddProfile(false);
    setShowIntro(false);
    toast.success('Profile created successfully');
  };

  const handleSelectProfile = (profile) => {
    setSelectedProfile(profile);
  };

  // Section definitions with 3D pastel icons
  const SECTIONS = [
    {
      id: 'medical',
      title: 'Medical Information',
      iconSrc: APP_ICONS.medicalInfo,
      bgColor: 'bg-red-50',
      path: `/emergency-binder/${selectedProfile?.id}/medical`,
      description: 'Allergies, medications, conditions, doctors',
    },
    {
      id: 'contacts',
      title: 'Emergency Contacts',
      iconSrc: APP_ICONS.familyContacts,
      bgColor: 'bg-purple-50',
      path: `/emergency-binder/${selectedProfile?.id}/contacts`,
      description: 'Family, caregivers, emergency contacts',
    },
    {
      id: 'documents',
      title: 'Important Documents',
      iconSrc: APP_ICONS.documents,
      bgColor: 'bg-blue-50',
      path: `/emergency-binder/${selectedProfile?.id}/documents`,
      description: 'IDs, insurance cards, certificates',
    },
    {
      id: 'household',
      title: 'Household & Life',
      iconSrc: APP_ICONS.pet,
      bgColor: 'bg-emerald-50',
      path: `/emergency-binder/${selectedProfile?.id}/household`,
      description: 'Home info, utilities, WiFi, pets',
    },
    {
      id: 'financial',
      title: 'Financial Overview',
      iconSrc: APP_ICONS.notes,
      bgColor: 'bg-amber-50',
      path: `/emergency-binder/${selectedProfile?.id}/financial`,
      description: 'Bank accounts, important financial info',
      optional: true,
    },
    {
      id: 'endoflife',
      title: 'End-of-Life Planning',
      iconSrc: APP_ICONS.notes,
      bgColor: 'bg-gray-50',
      path: `/emergency-binder/${selectedProfile?.id}/endoflife`,
      description: 'Will, power of attorney, preferences',
      optional: true,
    },
  ];

  // Get section status
  const getSectionStatus = (sectionId) => {
    if (!selectedProfile) return 'not_started';
    
    // DEMO MODE: Check demo data directly
    if (isDemoMode) {
      const demoAllergies = demo.getAllergies?.() || [];
      const demoMedications = demo.getMedications?.() || [];
      const demoContacts = demo.getEmergencyContacts?.() || [];
      const demoDocuments = demo.getDocuments?.() || [];
      const demoHousehold = demo.getHousehold?.() || {};
      const demoMedicalProfile = demo.getMedicalProfile?.() || {};
      
      switch (sectionId) {
        case 'medical':
          if (demoMedicalProfile.full_name || demoMedicalProfile.blood_type || 
              demoAllergies.length > 0 || demoMedications.length > 0) {
            if (demoMedicalProfile.full_name && demoMedicalProfile.blood_type && demoAllergies.length > 0) {
              return 'completed';
            }
            return 'incomplete';
          }
          return 'not_started';
        
        case 'contacts':
          if (demoContacts.length > 0) {
            return demoContacts.length >= 2 ? 'completed' : 'incomplete';
          }
          return 'not_started';
        
        case 'documents':
          return demoDocuments.length > 0 ? 'completed' : 'not_started';
        
        case 'household':
          if (demoHousehold.address || demoHousehold.wifi_network) {
            return 'completed';
          }
          return 'not_started';
        
        default:
          return 'not_started';
      }
    }
    
    // REAL MODE: Check profile data
    const profile = selectedProfile;
    
    switch (sectionId) {
      case 'medical':
        const medical = profile.medical_info || {};
        if (medical.full_name || medical.blood_type || 
            (medical.allergies && medical.allergies.length > 0) ||
            (medical.medications && medical.medications.length > 0)) {
          if (medical.full_name && medical.blood_type && medical.allergies?.length > 0) {
            return 'completed';
          }
          return 'incomplete';
        }
        return 'not_started';
      
      case 'contacts':
        const contacts = profile.emergency_contacts || [];
        if (contacts.length > 0) {
          return contacts.length >= 2 ? 'completed' : 'incomplete';
        }
        return 'not_started';
      
      case 'documents':
        const docs = profile.documents || [];
        return docs.length > 0 ? 'completed' : 'not_started';
      
      case 'household':
        const household = profile.household_info || {};
        if (household.home_address || household.wifi_network) {
          return 'completed';
        }
        return 'not_started';
      
      default:
        return 'not_started';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
      </div>
    );
  }

  // Show intro screen for new users
  if (showIntro) {
    return (
      <>
        <IntroScreen 
          onStart={handleStartBinder} 
          completion={overallCompletion}
          hasExistingProfiles={profiles.length > 0}
        />
        <AddProfileDialog
          open={showAddProfile}
          onOpenChange={setShowAddProfile}
          onProfileCreated={handleProfileCreated}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-32" data-testid="emergency-binder-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-50/80 border border-purple-100/50 shadow-sm flex items-center justify-center">
                <PastelIcon src={APP_ICONS.emergencyBinder} alt="Emergency Binder" size={36} />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-slate-800">Emergency Binder</h1>
                <p className="text-xs text-slate-500">Your digital life binder</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddProfile(true)}
              className="gap-1"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Add Profile</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Profile Selector */}
        <ProfileSelector
          profiles={profiles}
          selectedProfile={selectedProfile}
          onSelectProfile={handleSelectProfile}
          onAddProfile={() => setShowAddProfile(true)}
        />

        {/* Overall Progress - Updated colors: green/purple for completion, NOT red */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Life Binder Completion</span>
              <span className={`text-sm font-semibold ${
                overallCompletion === 100 
                  ? 'text-emerald-600' 
                  : overallCompletion >= 50 
                    ? 'text-purple-600' 
                    : 'text-slate-600'
              }`}>
                {overallCompletion}% Complete
              </span>
            </div>
            {/* Progress bar with green/purple indicator instead of red */}
            <div className="relative h-2 w-full overflow-hidden rounded-full bg-slate-200">
              <div 
                className={`h-full transition-all ${
                  overallCompletion === 100 
                    ? 'bg-emerald-500' 
                    : 'bg-purple-500'
                }`}
                style={{ width: `${overallCompletion}%` }}
              />
            </div>
            
            {/* Progress message */}
            <p className="text-xs text-slate-500 mt-2">
              {overallCompletion < 50 
                ? "Completing your Life Binder helps doctors and family in emergencies."
                : overallCompletion < 100
                  ? "You're almost finished preparing your Life Binder."
                  : "Your Life Binder is Ready!"}
            </p>
          </CardContent>
        </Card>

        {/* Emergency Summary */}
        {selectedProfile && (
          <EmergencySummary profileId={selectedProfile.id} profileName={selectedProfile.name} />
        )}

        {/* Binder Sections */}
        <section>
          <h3 className="text-sm font-medium text-slate-500 mb-3">Binder Sections</h3>
          <div className="space-y-3">
            {SECTIONS.map((section) => {
              const status = getSectionStatus(section.id);
              
              return (
                <Card
                  key={section.id}
                  className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(section.path)}
                  data-testid={`section-${section.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Icon with soft translucent background, subtle border/shadow */}
                      <div className={`w-14 h-14 rounded-xl ${section.bgColor} border border-slate-100/50 shadow-sm flex items-center justify-center flex-shrink-0`}>
                        <PastelIcon src={section.iconSrc} alt={section.title} size={40} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-slate-800">{section.title}</h4>
                          {section.optional && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">Optional</span>
                          )}
                        </div>
                        {/* Description: max 2 lines, ellipsis after complete word */}
                        <p className="text-sm text-slate-500 line-clamp-2">{section.description}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <StatusIcon status={status} />
                        <ChevronRight className="h-5 w-5 text-slate-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Quick Actions - Emergency Mode (RED reserved for this) and Share Summary */}
        <section className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            className="h-auto py-3 flex flex-col items-center gap-1.5 border-red-200 bg-red-50/50"
            onClick={() => navigate(`/emergency-binder/${selectedProfile?.id}/emergency-mode`)}
            data-testid="emergency-mode-btn"
          >
            <AlertCircle className="h-5 w-5 text-red-500" />
            <span className="text-xs font-medium text-red-700">Emergency Mode</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-3 flex flex-col items-center gap-1.5"
            onClick={() => navigate(`/emergency-binder/${selectedProfile?.id}/summary`)}
            data-testid="emergency-summary-btn"
          >
            <QrCode className="h-5 w-5 text-purple-500" />
            <span className="text-xs font-medium">Share Summary</span>
          </Button>
        </section>

        {/* Secondary Quick Actions - Compact height reduced ~15% */}
        <section className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            className="h-auto py-2.5 flex flex-col items-center gap-1.5"
            onClick={() => navigate(`/emergency-binder/${selectedProfile?.id}/doctor-summary`)}
            data-testid="doctor-summary-btn"
          >
            <FileText className="h-4 w-4 text-blue-500" />
            <span className="text-[11px] font-medium">Doctor Summary</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-2.5 flex flex-col items-center gap-1.5"
            onClick={() => navigate(`/emergency-binder/${selectedProfile?.id}/sharing`)}
            data-testid="family-sharing-btn"
          >
            <Share2 className="h-4 w-4 text-emerald-500" />
            <span className="text-[11px] font-medium">Profile Sharing</span>
          </Button>
        </section>

        {/* Family & Caregiver Actions - Compact height reduced ~15% */}
        <section className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            className="h-auto py-2.5 flex flex-col items-center gap-1.5 border-purple-200 bg-purple-50/50"
            onClick={() => navigate('/emergency-binder/family-network')}
            data-testid="family-network-btn"
          >
            <Users className="h-4 w-4 text-purple-600" />
            <span className="text-[11px] font-medium text-purple-800">Network</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-2.5 flex flex-col items-center gap-1.5 border-teal-200 bg-teal-50/50"
            onClick={() => navigate('/emergency-binder/caregivers')}
            data-testid="caregiver-btn"
          >
            <UserCog className="h-4 w-4 text-teal-600" />
            <span className="text-[11px] font-medium text-teal-800">Caregivers</span>
          </Button>
        </section>

        {/* Additional Quick Actions - 3-button row with centered icons and clean labels */}
        <section className="grid grid-cols-3 gap-2">
          <Button
            variant="outline"
            className="h-auto py-2.5 flex flex-col items-center justify-center gap-1"
            onClick={() => navigate(`/emergency-binder/${selectedProfile?.id}/lock-screen-card`)}
            data-testid="lock-screen-btn"
          >
            <Smartphone className="h-4 w-4 text-slate-600" />
            <span className="text-[10px] font-medium text-center leading-tight">Lock Screen</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-2.5 flex flex-col items-center justify-center gap-1"
            onClick={() => navigate(`/emergency-binder/${selectedProfile?.id}/print`)}
            data-testid="print-binder-btn"
          >
            <Printer className="h-4 w-4 text-slate-600" />
            <span className="text-[10px] font-medium text-center leading-tight">Print Binder</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-2.5 flex flex-col items-center justify-center gap-1"
            onClick={() => window.open(`/emergency-binder/${selectedProfile?.id}/emergency-mode`, '_blank')}
            data-testid="fullscreen-emergency-btn"
          >
            <Maximize2 className="h-4 w-4 text-slate-600" />
            <span className="text-[10px] font-medium text-center leading-tight">Expand</span>
          </Button>
        </section>
      </main>

      {/* Add Profile Dialog */}
      <AddProfileDialog
        open={showAddProfile}
        onOpenChange={setShowAddProfile}
        onProfileCreated={handleProfileCreated}
      />
    </div>
  );
};

export default EmergencyBinderPage;
